import { Component, Fragment, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { fmtTL } from "../../lib/format";
import { num, diff, diffInc } from "./dailyBudgetCalc";

const KIND_META = {
  invoice: { label: "Fatura", to: "/veri-girisi?bolum=fatura" },
  check: { label: "Çek", to: "/veri-girisi?bolum=cek" },
  card: { label: "Ekstre", to: "/veri-girisi?bolum=kredi&alt=kart" },
  loan: { label: "Kredi", to: "/veri-girisi?bolum=kredi" },
};

export function PlanDayCell({ plan, realizations, day, kind, todayDay }) {
  if (!plan) return <span className="text-slate-400">-</span>;
  const income = kind === "income";
  const total = realizations.reduce((s, r) => s + r.amount, 0);
  const left = income ? total - plan : plan - total;
  if (!realizations.length) {
    const passed = Number(day) < todayDay;
    const lateText = `Planlandı ama gerçekleşmedi · henüz ${income ? "tahsil edilmedi" : "ödenmedi"}`;
    return (
      <span className={`inline-flex flex-col items-end leading-tight whitespace-nowrap ${passed ? "text-red-700" : "text-slate-500"}`} data-testid={passed ? "plan-late-cell" : "plan-waiting-cell"}>
        <span className="inline-flex items-center gap-1 font-bold">{passed && <AlertTriangle className="w-3 h-3" />}Plan {fmtTL(plan)}</span>
        <span className="text-[9px] font-bold uppercase">{passed ? lateText : "bekleniyor"}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-end leading-tight whitespace-nowrap" data-testid="plan-day-cell">
      <span className="text-slate-900 font-bold"><span className="text-[9px] text-slate-400 font-normal mr-1">Toplam plan</span>{fmtTL(plan)}</span>
      {realizations.map((r) => <span key={r.day} className="text-[10px] text-slate-600">{r.day}. gün {fmtTL(r.amount)} {income ? "tahsil edildi" : "ödendi"}</span>)}
      <span className={`text-[10px] font-bold ${left < 0 ? "text-red-700" : "text-emerald-700"}`}>Kalan {left > 0 ? "+" : ""}{fmtTL(left)}</span>
    </span>
  );
}

// kayda gidiş bağlantısı: ESC ile aynı gün + alt kalemin açık haline geri dönülür (?don=/butce?odak=<gün>-<kalem>)
const recordLink = (kind, id, odak) => `${KIND_META[kind]?.to || "/"}&kayit=${id}&don=${encodeURIComponent(`/butce?odak=${odak}`)}`;

function LeafRecords({ leafId, day, records }) {
  return (
    <tr className="border-t border-slate-100 bg-sky-50/40">
      <td colSpan={9} className="px-8 py-2" data-testid={`leaf-records-${leafId}`}>
        {records.length > 0 ? (
          <div className="flex flex-col gap-1">
            {records.map((r, i) => (
              <Link key={r.id || i} to={recordLink(r.kind, r.id, `${day}-${leafId}`)} className="inline-flex items-center gap-1.5 text-[11px] text-sky-700 hover:underline w-fit" data-testid={`leaf-record-${leafId}-${i}`}>
                <span className="px-1 py-0.5 rounded bg-sky-100 text-sky-800 text-[9px] font-bold uppercase">{KIND_META[r.kind]?.label || r.kind}</span>
                {r.label}
              </Link>
            ))}
          </div>
        ) : (
          <span className="text-[11px] text-slate-400">Bu güne ait kayıt yok</span>
        )}
      </td>
    </tr>
  );
}

function LeafRow({ c, it, day, calc, todayDay, leafOpen, onToggle }) {
  const d = calc.pd(c.id);
  const here = d.day === Number(day);
  const records = Array.isArray(it?.records) ? it.records : [];
  return (
    <Fragment>
      <tr onClick={onToggle} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" data-testid={`day-${Number(day)}-leaf-${c.id}`} title="Kayıtları görmek için tıklayın">
        <td></td>
        <td></td>
        <td className="px-3 py-1.5 text-xs text-slate-700">
          <span className="inline-flex items-center gap-1">{leafOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}{c.name}</span>
        </td>
        <td className="numeric-cell px-3 py-1.5 text-xs text-slate-600">{here ? <PlanDayCell plan={d.income} realizations={calc.realizations(c.id, "income")} day={day} kind="income" todayDay={todayDay} /> : "-"}</td>
        <td className="numeric-cell px-3 py-1.5 text-xs text-emerald-700">{num(it?.income || 0)}</td>
        <td className="numeric-cell px-3 py-1.5 text-xs text-slate-600">{here && d.income ? diffInc(d.income, calc.monthTotal(c.id, "income")) : "-"}</td>
        <td className="numeric-cell px-3 py-1.5 text-xs text-slate-600">{here ? <PlanDayCell plan={d.expense} realizations={calc.realizations(c.id, "expense")} day={day} kind="expense" todayDay={todayDay} /> : "-"}</td>
        <td className="numeric-cell px-3 py-1.5 text-xs text-red-700">{num(it?.expense || 0)}</td>
        <td className="numeric-cell px-3 py-1.5 text-xs text-slate-600">{here && d.expense ? diff(d.expense, calc.monthTotal(c.id, "expense")) : "-"}</td>
      </tr>
      {leafOpen && <LeafRecords leafId={c.id} day={day} records={records} />}
    </Fragment>
  );
}

// Bir ana kalem + altındaki ilgili alt kalem satırları (tek gün için)
export function ParentRows({ p, kids, dayItems, day, calc, leafRelevant, todayDay, openLeaves, toggleLeaf }) {
  const leafKids = useMemo(() => kids.filter(leafRelevant), [kids, leafRelevant]);
  const { pd, monthTotal } = calc;
  const here = kids.filter((c) => pd(c.id).day === Number(day));
  const pPlanInc = here.reduce((s, c) => s + pd(c.id).income, 0);
  const pPlan = here.reduce((s, c) => s + pd(c.id).expense, 0);
  const pTotInc = here.filter((c) => pd(c.id).income > 0).reduce((s, c) => s + monthTotal(c.id, "income"), 0);
  const pTotExp = here.filter((c) => pd(c.id).expense > 0).reduce((s, c) => s + monthTotal(c.id, "expense"), 0);
  const pInc = kids.reduce((s, c) => s + (dayItems?.[c.id]?.income || 0), 0);
  const pExp = kids.reduce((s, c) => s + (dayItems?.[c.id]?.expense || 0), 0);
  return (
    <>
      <tr className="border-t border-slate-200 bg-slate-50/60" data-testid={`day-${Number(day)}-parent-${p.id}`}>
        <td></td>
        <td className="px-3 py-2 text-xs font-bold text-slate-900">{p.name}</td>
        <td></td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold">{num(pPlanInc)}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold text-emerald-800">{num(pInc)}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold text-slate-900">{pPlanInc ? diffInc(pPlanInc, pTotInc) : "-"}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold">{num(pPlan)}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold text-red-800">{num(pExp)}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold text-slate-900">{pPlan ? diff(pPlan, pTotExp) : "-"}</td>
      </tr>
      {leafKids.map((c) => {
        const leafKey = `${day}-${c.id}`;
        return <LeafRow key={c.id} c={c} it={dayItems?.[c.id]} day={day} calc={calc} todayDay={todayDay} leafOpen={!!openLeaves[leafKey]} onToggle={() => toggleLeaf(leafKey)} />;
      })}
    </>
  );
}

// Bir günün satırlarında oluşan hata tüm sayfayı düşürmesin — satır içi uyarı göster
export class RowsGuard extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) {
      return (
        <tr className="border-t border-red-200 bg-red-50" data-testid={`day-${Number(this.props.day)}-error`}>
          <td colSpan={9} className="px-6 py-2 text-[11px] text-red-700 font-semibold">{this.props.label}: satırlar gösterilemedi — {String(this.state.err?.message || this.state.err)}</td>
        </tr>
      );
    }
    return this.props.children;
  }
}

// Bir günün tbody'si: gün başlık satırı + açıkken ana/alt kalem satırları
export function DayBody({ d, dayItems, open, onToggle, parents, childrenOf, leafs, calc, totalPlan, cumDay, todayDay, openLeaves, toggleLeaf }) {
  const { pd, monthTotal, lateLeaves } = calc;
  const dayIncome = Object.values(dayItems || {}).reduce((s, v) => s + (v?.income || 0), 0);
  const dayExpense = Object.values(dayItems || {}).reduce((s, v) => s + (v?.expense || 0), 0);
  const plannedHere = leafs.filter((l) => pd(l.id).day === Number(d));
  const planInc = plannedHere.reduce((s, l) => s + pd(l.id).income, 0);
  const planExp = plannedHere.reduce((s, l) => s + pd(l.id).expense, 0);
  const totInc = plannedHere.filter((l) => pd(l.id).income > 0).reduce((s, l) => s + monthTotal(l.id, "income"), 0);
  const totExp = plannedHere.filter((l) => pd(l.id).expense > 0).reduce((s, l) => s + monthTotal(l.id, "expense"), 0);
  const leafRelevant = useCallback((c) => (dayItems?.[c.id]?.income || 0) > 0 || (dayItems?.[c.id]?.expense || 0) > 0 || ((pd(c.id).income > 0 || pd(c.id).expense > 0) && pd(c.id).day === Number(d)), [dayItems, pd, d]);
  const visibleParents = parents.filter((p) => (childrenOf[p.id] || []).some(leafRelevant));
  const late = lateLeaves(d);
  const overshoot = totalPlan > 0 && cumDay?.exp > totalPlan;
  return (
    <tbody>
      <tr className="border-t border-slate-200 bg-white hover:bg-slate-50 cursor-pointer select-none" onClick={onToggle} data-testid={`day-row-${Number(d)}`}>
        <td className="px-3 py-2 text-xs font-bold text-slate-900 whitespace-nowrap">
          <span className="inline-flex items-center gap-1.5">{open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}{Number(d)}. GÜN
            {overshoot && (
              <span className="ml-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold uppercase tracking-wide" data-testid={`day-overshoot-${Number(d)}`} title={`Planlı kalemlerde aya kümülatif gerçekleşen (${fmtTL(cumDay.exp)}) aylık planı (${fmtTL(totalPlan)}) aştı`}>Plan Aşımı</span>
            )}
            {late.length > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold uppercase tracking-wide animate-pulse" data-testid={`day-late-${Number(d)}`} title={`Planlanan gün geçti, gerçekleşmedi: ${late.map((l) => l.name).join(", ")}`}><AlertTriangle className="w-3 h-3" /> Gecikti ({late.length})</span>
            )}
          </span>
        </td>
        <td colSpan={2} className="px-3 py-2 text-[11px] text-slate-400">{open ? "" : `${visibleParents.length ? `${visibleParents.length} ana kalem` : "kayıt yok"}`}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold">{num(planInc)}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-semibold text-emerald-700">{num(dayIncome)}</td>
        <td className="numeric-cell px-3 py-2 text-xs text-slate-500">{planInc ? diffInc(planInc, totInc) : "-"}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-bold">{num(planExp)}</td>
        <td className="numeric-cell px-3 py-2 text-xs font-semibold text-red-700">{num(dayExpense)}</td>
        <td className="numeric-cell px-3 py-2 text-xs text-slate-500">{planExp ? diff(planExp, totExp) : "-"}</td>
      </tr>
      {open && visibleParents.length === 0 && (
        <tr className="border-t border-slate-100"><td colSpan={9} className="px-6 py-2 text-[11px] text-slate-400" data-testid={`day-empty-${Number(d)}`}>Bu güne ait kayıt veya plan yok</td></tr>
      )}
      {open && visibleParents.map((p) => (
        <RowsGuard key={p.id} label={p.name} day={d}>
          <ParentRows p={p} kids={childrenOf[p.id] || []} dayItems={dayItems} day={d} calc={calc} leafRelevant={leafRelevant} todayDay={todayDay} openLeaves={openLeaves} toggleLeaf={toggleLeaf} />
        </RowsGuard>
      ))}
    </tbody>
  );
}
